import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiVisitEngine } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/query-keys'
import { useAuthStore } from '@/shared/store/authStore'
import { getOrganizationTinFromUser } from '@/shared/utils/get-organization-id'
import type { IApiListResponse, IApiResponse } from '@/shared/types/api'
import type {
  IOrganization,
  IOrganizationListParams,
  IOrganizationPayload,
  IOrganizationUpdatePayload,
} from '../model/organization.type'
import { BLOOD_CENTER_TYPE_CODE } from '../model/organization.type'

/** Visit-engine's base URL already carries the `/api` root. */
const ORGANIZATIONS_PATH = '/v1/organizations'

/** Unset filters are dropped so they never reach the query as `undefined`. */
const toQuery = ({ sort, ...params }: IOrganizationListParams) => {
  const query = new URLSearchParams()
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    query.append(key, String(value))
  })
  sort?.forEach((entry) => query.append('sort', entry))
  return query.toString()
}

// ── Raw API ───────────────────────────────────────────────────────────────────

export const organizationApi = {
  /** Returns the whole envelope — callers need `totalSize` / `hasNext`. */
  getOrganizations: async (params: IOrganizationListParams = {}) => {
    const response = await apiVisitEngine.get<IApiListResponse<IOrganization>>(
      `${ORGANIZATIONS_PATH}?${toQuery(params)}`,
    )
    return response.data
  },

  getOrganizationByInn: async (inn: string) => {
    const response = await apiVisitEngine.get<IApiResponse<IOrganization>>(
      `${ORGANIZATIONS_PATH}/${inn}`,
    )
    return response.data.data
  },

  createOrganization: async (payload: IOrganizationPayload) => {
    const response = await apiVisitEngine.post<IApiResponse<IOrganization>>(
      ORGANIZATIONS_PATH,
      payload,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data.data
  },

  updateOrganization: async (
    inn: string,
    payload: IOrganizationUpdatePayload,
  ) => {
    const response = await apiVisitEngine.put<IApiResponse<IOrganization>>(
      `${ORGANIZATIONS_PATH}/${inn}`,
      payload,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data.data
  },

  /** Soft delete — `active = false`, visits and history are kept. */
  deactivateOrganization: async (inn: string) => {
    const response = await apiVisitEngine.delete<IApiResponse<boolean>>(
      `${ORGANIZATIONS_PATH}/${inn}`,
    )
    return response.data.data
  },

  /** One-time INN for an organization auto-created from a DHP login. */
  assignOrganizationInn: async (id: string, inn: string) => {
    const response = await apiVisitEngine.patch<IApiResponse<IOrganization>>(
      `${ORGANIZATIONS_PATH}/without-inn/${id}`,
      { inn },
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data.data
  },
}

// ── Query hooks ───────────────────────────────────────────────────────────────

export const useGetOrganizationsQuery = (
  params: IOrganizationListParams = {},
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: queryKeys.organizations.list(params),
    queryFn: () => organizationApi.getOrganizations(params),
    placeholderData: (previous) => previous,
    enabled: options?.enabled ?? true,
  })

export const useGetOrganizationQuery = (inn: string | null | undefined) =>
  useQuery({
    queryKey: queryKeys.organizations.detail(inn ?? ''),
    queryFn: () => organizationApi.getOrganizationByInn(inn!),
    enabled: !!inn,
    retry: false,
  })

/**
 * The caller's own organization, looked up by the INN in the token — the token
 * carries no organization type, the registry does (BS-295). `isBloodCenter` is
 * `undefined` until the registry answers, and stays so if it cannot.
 */
export const useCurrentOrganizationQuery = () => {
  const user = useAuthStore((s) => s.user)
  const inn = getOrganizationTinFromUser(user)
  const query = useQuery({
    queryKey: queryKeys.organizations.detail(inn),
    queryFn: () => organizationApi.getOrganizationByInn(inn),
    enabled: !!inn,
    retry: false,
    staleTime: Infinity,
  })
  const typeCode = query.data?.organizationType?.code
  return {
    ...query,
    inn,
    isBloodCenter: typeCode ? typeCode === BLOOD_CENTER_TYPE_CODE : undefined,
  }
}

/**
 * Active blood stations for pickers (applications, issuance, journal filters)
 * — replaces production-core's removed `organization-codes` dictionary. The
 * list is short and rarely changes, so one response serves the session.
 */
export const useGetBloodStationsQuery = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: queryKeys.organizations.stations(),
    queryFn: async () => {
      const response = await organizationApi.getOrganizations({
        organizationTypeCode: BLOOD_CENTER_TYPE_CODE,
        active: true,
        size: 100,
      })
      return response.data
    },
    staleTime: Infinity,
    enabled: options?.enabled ?? true,
  })

// ── Mutation hooks ────────────────────────────────────────────────────────────

const useInvalidateOrganizations = () => {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all })
}

export const useCreateOrganizationMutation = () => {
  const invalidate = useInvalidateOrganizations()
  return useMutation({
    mutationFn: (payload: IOrganizationPayload) =>
      organizationApi.createOrganization(payload),
    onSuccess: invalidate,
  })
}

export const useUpdateOrganizationMutation = () => {
  const invalidate = useInvalidateOrganizations()
  return useMutation({
    mutationFn: ({
      inn,
      payload,
    }: {
      inn: string
      payload: IOrganizationUpdatePayload
    }) => organizationApi.updateOrganization(inn, payload),
    onSuccess: invalidate,
  })
}

export const useDeactivateOrganizationMutation = () => {
  const invalidate = useInvalidateOrganizations()
  return useMutation({
    mutationFn: (inn: string) => organizationApi.deactivateOrganization(inn),
    onSuccess: invalidate,
  })
}

export const useAssignOrganizationInnMutation = () => {
  const invalidate = useInvalidateOrganizations()
  return useMutation({
    mutationFn: ({ id, inn }: { id: string; inn: string }) =>
      organizationApi.assignOrganizationInn(id, inn),
    onSuccess: invalidate,
  })
}
